export function renderGenBulkSortOrderContentServerActionTemplate({
  ctxTableVar,
  ctxCacheInvalidationFn,
  ctxCamelPlural,
  lifecycleHookImport,
  ctxPlural,
  reorderContext,
  beforeHook,
  afterHook
}: {
  ctxTableVar: string
  ctxCacheInvalidationFn: string
  ctxCamelPlural: string
  lifecycleHookImport: string
  ctxPlural: string
  reorderContext: string
  beforeHook: string
  afterHook: string
}): string {
  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${ctxTableVar} } from '@admin/db/schema'
import { asc, eq, notInArray } from 'drizzle-orm'
import { ${ctxCacheInvalidationFn} } from 'next/cache'
import { ${ctxCamelPlural}CacheTags } from './types'
${lifecycleHookImport}

export async function bulkUpdate${ctxPlural}SortOrder(
  updates: Array<{ id: string; sortOrder: number }>
): Promise<{ success: boolean; error?: string }> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])
  if (updates.length === 0) return { success: true }

  const now = new Date().toISOString()
  const auditRequestMetadata = await getAuditRequestMetadata()
  const lifecycleContext = ${reorderContext}
  ${beforeHook}

  try {
    const sorted = [...updates].sort((a, b) => a.sortOrder - b.sortOrder)
    const updatedIds = sorted.map((u) => u.id)

    const remaining = await db
      .select({ id: ${ctxTableVar}.id })
      .from(${ctxTableVar})
      .where(updatedIds.length > 0 ? notInArray(${ctxTableVar}.id, updatedIds) : undefined)
      .orderBy(asc(${ctxTableVar}.sortOrder))

    const maxUpdatedPosition = Math.max(...sorted.map((u) => u.sortOrder))
    const allIds: string[] = new Array(maxUpdatedPosition + 1 + remaining.length)

    for (const u of sorted) {
      allIds[u.sortOrder] = u.id
    }

    let fillIndex = 0
    let remainingIndex = 0
    while (fillIndex <= maxUpdatedPosition && remainingIndex < remaining.length) {
      if (allIds[fillIndex] === undefined) {
        allIds[fillIndex] = remaining[remainingIndex].id as string
        remainingIndex++
      }
      fillIndex++
    }
    let appendIndex = maxUpdatedPosition + 1
    while (remainingIndex < remaining.length) {
      allIds[appendIndex] = remaining[remainingIndex].id as string
      remainingIndex++
      appendIndex++
    }

    const appliedOrder = allIds
      .filter(Boolean)
      .map((id, index) => ({ id, sortOrder: index }))

    await Promise.all(
      appliedOrder.map(({ id, sortOrder }) =>
        db.update(${ctxTableVar}).set({ sortOrder }).where(eq(${ctxTableVar}.id, id))
      )
    )

    ${ctxCacheInvalidationFn}(${ctxCamelPlural}CacheTags.all)
    ${afterHook}
    return { success: true }
  } catch (error) {
    console.error('Error bulk updating sort order:', error)
    return { success: false, error: error instanceof Error ? error.message : 'Failed to bulk update sort order' }
  }
}
`
}
